import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { useFeatures } from 'components/FeaturesContext';
import { useUserContext } from 'components/UserContext';
import { useCreateCheckoutSession } from 'controllers/react-query/paymentHooks';

export type SubscriptionType = 'annual' | 'monthly';

type SubscribeButtonProps = {
  subscription: SubscriptionType;
};

/**
 * Component to create a checkout session
 */
export const SubscribeButton = ({ subscription }: SubscribeButtonProps) => {
  const { mutate: createCheckoutSession } = useCreateCheckoutSession();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirectUrl') || '/';

  const handleSubscribe = () => {
    createCheckoutSession(
      { payload: { subscription, redirectUrl } },
      {
        onSuccess: data => {
          window.location.href = data.url;
        },
      },
    );
  };

  return (
    <Button onClick={handleSubscribe} variant='contained' color='primary'>
      Subscribe Now
    </Button>
  );
};

/**
 * Component to navigate to the pricing page with a redirect URL for checkout
 */
export const NavigateToPricingButton = ({ children, ...props }: { children?: React.ReactNode } & ButtonProps) => {
  const location = useLocation();
  return (
    <Button
      component={NavLink}
      {...props}
      to={`/pricing?redirectUrl=${encodeURIComponent(location.pathname)}`}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        props.onClick?.(e);
      }}
    >
      {children}
    </Button>
  );
};

/**
 * Hook to determine if a user requires a subscription
 */
export const usePaywall = () => {
  const { user } = useUserContext();
  const { test_mode__always_show_paywall: testPaywallEnabled, show_stripe_payments: showStripePayments } =
    useFeatures();
  const userRequiresSubscription = showStripePayments && (user?.requires_subscription || testPaywallEnabled);

  return { userRequiresSubscription };
};
